
import * as THREE from "three";

// Handle size input changes (width/height)
export const updateDesignSize = (value: number, min = 0.2, max = 2) => {
  return Math.max(min, Math.min(max, value));
};

// Constrain rotation value within 0-359 range
export const constrainRotation = (value: number): number => {
  return ((value % 360) + 360) % 360;
};

// Scale design position between 0-100 range (for UI) to -0.5 to 0.5 range (for THREE.js)
export const positionToOffset = (position: { x: number, y: number }): { x: number, y: number } => {
  // Enhanced mapping for better design placement
  return {
    x: ((position.x - 50) / 100) * 1.0, // Full horizontal range
    y: ((position.y - 50) / 100) * 1.2, // Enhanced vertical range for better placement
  };
};

// Calculate design scales based on design dimensions and target size
export const calculateDesignScale = (
  designWidth: number, 
  designHeight: number, 
  targetSize: number = 0.8
): number => {
  // Default scale if dimensions are not available
  if (!designWidth || !designHeight) return targetSize;
  
  const aspectRatio = designWidth / designHeight;
  
  // Adjust scale based on aspect ratio
  if (aspectRatio > 1.5) {
    // Wide design - scale based on width
    return targetSize * 0.8;
  } else if (aspectRatio < 0.7) {
    // Tall design - scale based on height
    return targetSize * 1.2;
  }
  
  // Square-ish design
  return targetSize;
};

// Calculate the best position for a design based on dimensions and view
export const getOptimalDesignPosition = (
  isBackView: boolean = false
): { x: number, y: number } => {
  // Position slightly higher on the front for chest placement
  // Position slightly lower on the back
  return isBackView 
    ? { x: 50, y: 45 } // Back position
    : { x: 50, y: 38 }; // Front position (higher up on chest)
};

// Apply design position to texture
export const applyPositionToTexture = (
  texture: THREE.Texture | null,
  position: { x: number, y: number }
): void => {
  if (!texture) return;
  
  const { x, y } = positionToOffset(position);
  // Negate Y because texture coordinates and design coordinates use different systems
  texture.offset.set(x, -y);
};

// Apply rotation to texture
export const applyRotationToTexture = (
  texture: THREE.Texture | null,
  rotation: number
): void => {
  if (!texture) return;
  
  // Convert degrees to radians
  const radians = (rotation * Math.PI) / 180;
  texture.rotation = radians;
  
  // When rotating, we need to set the center of rotation
  texture.center.set(0.5, 0.5);
};

// Apply scaling to texture
export const applyScalingToTexture = (
  texture: THREE.Texture | null,
  designSize: number
): void => {
  if (!texture) return;

  // Apply scaling with adjusted values for better visibility
  const scale = 1.0 / (designSize * 1.4);
  texture.repeat.set(scale, scale);
}
